import { useCallback, useEffect, useRef, useState } from 'react';
import { BALANCED, REGULARS } from '../../ai';
import {
  CampaignError,
  LONG_NIGHT,
  abandonNightRun,
  currentTable,
  fieldNight,
  finishNightTable,
  puzzlePosition,
  recordNightAction,
  setFielding,
  solvePuzzle,
  startNightRun,
  tableDifficulty,
  withLedger,
  type DetourId,
  type NightMatch,
  type NightOutcome,
  type NightRun,
  type Profile,
} from '../../campaign';
import { applyAction, startMatch, type Action, type MatchEvent, type Replay } from '../../engine';
import { t } from '../../i18n';
import type { AiClient } from '../../app/aiClient';
import { loadProfile, newProfile, saveProfile } from '../../app/campaignStore';
import { guideSeen, markGuideSeen } from '../../app/guides';
import { MatchHost } from '../../app/matchHost';
import { MatchLogger, hostSource } from '../../app/matchLog';
import type { Settings } from '../../app/settings';
import { ConfirmDialog } from '../components/MatchDialogs';
import { NightScreen, Section } from '../components/NightCounter';
import { DetourScene } from '../components/night/DetourScene';
import { SpoilsScene } from '../components/night/SpoilsScene';
import { RunEndScene } from '../components/night/RunEndScene';
import { CounterScene } from '../components/night/CounterScene';
import { NightGuide } from '../components/night/NightGuide';
import { NightIcon } from '../components/night/NightIcon';
import { formatScore } from '../components/ScoreBurst';
import { sounds } from '../../app/sound';
import {
  nightScene,
  REGULAR_LAMP,
  REGULAR_SMOKE,
  resetScene,
  setScene,
  type Scene,
  type SmokeName,
} from '../scene';
import { BoardScene } from '../components/night/BoardScene';
import { HubScene } from '../components/night/HubScene';
import { NightTitleCard, Rise } from '../components/night/Hud';
import { LedgerBook, type LedgerPage } from '../components/night/LedgerBook';
import { Receipt } from '../components/night/Receipt';
import {
  nightLabel,
  nightTitle,
  opponentName,
  opponentProp,
  tableKindName,
} from '../components/night/runText';
import { MatchScreen } from './MatchScreen';

/**
 * The Long Night (docs/SHOP_DESIGN.md): a run of three Nights of three tables, the Counter
 * between them, Spoils and Detours after each High Table, Closing Time against the Broker, and
 * After Hours. The run lives in the campaign profile and is saved after every change.
 */

/** Every event of a table so far, replayed from its seed (the run needs all of them at the end). */
function tableEvents(m: NightMatch): MatchEvent[] {
  const start = startMatch(m.config, m.seed);
  let s = start.state;
  const out: MatchEvent[] = [...start.events];
  for (const a of m.actions) {
    const r = applyAction(s, a);
    if (r.error) break;
    s = r.state;
    out.push(...r.events);
  }
  return out;
}

/** What each pawn wears at the table: a seam per homecoming, and its Hallmark (§12.10). */
function pawnMarks(p: Profile): Record<string, { seams: number; hallmark: boolean }> {
  const out: Record<string, { seams: number; hallmark: boolean }> = {};
  for (const pawn of Object.values(p.pawns)) {
    const seams = pawn.homecomings ?? 0;
    const hallmark = !!p.ledger?.hallmarks[pawn.id];
    if (seams > 0 || hallmark) out[pawn.id] = { seams, hallmark };
  }
  return out;
}

/** Each Detour's light (docs/LONG_NIGHT_ART_DIRECTION.md §11.15). */
const DETOUR_LIGHT: Readonly<Record<DetourId, Partial<Scene>>> = {
  // Amber, a carver's bench.
  workbench: { lampTint: '#ffb35a', tint: '#5a3a22' },
  // Embers.
  furnace: { lampTint: '#ff8a4a', tint: '#7a2a1a', speed: 1.2 },
  // Spectral green, a round table.
  seance: { lampTint: '#9cf2d4', tint: '#1e5a48', speed: 0.6 },
  // Cold, from straight above: no lamp, no hands.
  'puzzle-box': { lamp: 0, tint: '#164a52', stars: 0.4 },
  // A dim lantern.
  'lost-and-found': { lamp: 0.45, lampTint: '#e9c27a', narrow: true },
  // A mirror down the middle.
  twins: { lampTint: '#d9dde6' },
  // Moonlight instead of the lamp.
  fence: { lampTint: '#b8c8ff', tint: '#17338c', lamp: 0.7 },
  // The three regulars around the room.
  auction: { lampTint: '#ffd49a', speed: 0.7 },
  // Light falling through a lattice.
  confessional: { lampTint: '#ffe7c0', narrow: true, speed: 0.5 },
  // A map table.
  cartographer: { lampTint: '#ffdca0', tint: '#6e5223' },
};

/** The smoke of a boss with a power, After Hours (Closing Time keeps its own). */
const HORROR_SMOKE: Readonly<Record<string, SmokeName>> = {
  storm: 'storm',
  'turned-table': 'turnedTable',
  switch: 'switch',
  'lights-out': 'lightsOut',
  'clean-slate': 'cleanSlate',
  'blind-eye': 'closing',
};

/** The scene for where the run is (src/ui/scene.ts). */
function sceneOf(run: NightRun | null, atTable: boolean): Partial<Scene> {
  if (!run) return { ...nightScene(1), focus: 0.4 };
  const night = nightScene(run.night);
  const debt = run.brass < 0;
  const table = run.board[run.table];
  switch (run.phase) {
    case 'won':
      // Dawn: the only time the void is lit from below (§11.18).
      return run.night > LONG_NIGHT.NIGHTS + 1
        ? { ...nightScene(run.night), focus: 0.4 }
        : { smoke: 'dawn', lamp: 0.35, stars: 0.3, twinkle: false, focus: 0.85 };
    case 'lost':
      // A lost run: the table's own smoke drained of color, turning slowly backward (§6.4).
      return {
        ...night,
        lamp: 0,
        gutter: true,
        desaturate: 0.7,
        speed: 0.4,
        reverse: true,
        twinkle: false,
      };
    case 'counter':
      return { ...night, speed: 0.5, twist: 0.8, debt, focus: 0.42 };
    case 'detour':
      // Each Detour has its own light (§11.15).
      return {
        ...night,
        debt,
        focus: 0.4,
        ...(run.detour?.chosen ? DETOUR_LIGHT[run.detour.chosen] : {}),
      };
    case 'playing':
    case 'result': {
      if (!atTable && run.phase === 'result') return { ...night, debt, focus: 0.45 };
      // After Hours: a boss with a power brings its own weather (night/Horrors.tsx).
      const horror = table?.rules.find((r) => r.id in HORROR_SMOKE)?.id;
      if (horror && table?.kind === 'high')
        return { ...night, smoke: HORROR_SMOKE[horror]!, debt, focus: 0.45 };
      if (table?.regular !== undefined && table.kind === 'high')
        return {
          ...night,
          smoke: REGULAR_SMOKE[table.regular] ?? night.smoke,
          lampTint: REGULAR_LAMP[table.regular] ?? null,
          iridescent: table.regular === 1,
          debt,
          focus: 0.45,
        };
      return { ...night, debt, focus: 0.45 };
    }
    default:
      return { ...night, debt, focus: 0.5 };
  }
}

export function LongNightScreen({
  ai,
  delay,
  settings,
  reduceMotion,
  onExit,
  onExportReplay,
}: {
  ai: AiClient;
  delay: (action?: Action) => number;
  settings: Settings;
  reduceMotion: boolean;
  onExit: (notice?: string) => void;
  onExportReplay: (r: Replay) => void;
}) {
  const [profile, setProfileState] = useState<Profile>(() => {
    const loaded = loadProfile() ?? newProfile();
    const p = withLedger(loaded);
    if (p !== loaded || !loadProfile()) saveProfile(p);
    return p;
  });
  const profileRef = useRef(profile);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<'abandon' | null>(null);
  const [host, setHost] = useState<MatchHost | null>(null);
  const [puzzle, setPuzzle] = useState<MatchHost | null>(null);
  const [showResult, setShowResult] = useState(false);
  // The guide opens by itself on the first visit, and from the top bar at any time.
  const [guide, setGuide] = useState(() => !guideSeen('night'));
  // The Ledger book, open at a page.
  const [book, setBook] = useState<LedgerPage | null>(null);
  // Between Nights the next Night's title is set alone, in the dark (§11.16).
  const [shownNight, setShownNight] = useState(() => profile.ledger?.run?.night ?? 0);
  const [titleNight, setTitleNight] = useState<number | null>(null);

  const setProfile = useCallback((next: Profile) => {
    profileRef.current = next;
    setProfileState(next);
    const err = saveProfile(next);
    const failed = err ? t('error.storage', { message: err }) : null;
    setSaveError(failed);
    if (failed) setError(failed);
  }, []);

  const apply = useCallback(
    (fn: (p: Profile) => Profile) => {
      setError(null);
      try {
        setProfile(fn(profileRef.current));
      } catch (e) {
        setError(
          e instanceof CampaignError ? e.message : t('error.generic', { message: String(e) }),
        );
      }
    },
    [setProfile],
  );

  /** Opens (or resumes) the table stored in the run. */
  const openTable = useCallback(
    (p: Profile): MatchHost | null => {
      const run = p.ledger?.run;
      const match = run?.match;
      if (!run || !match) return null;
      const table = currentTable(run);
      const personality =
        table.regular !== undefined ? (REGULARS[table.regular] ?? BALANCED) : BALANCED;
      let logger: MatchLogger | null = null;
      const h = new MatchHost(
        {
          config: match.config,
          seed: match.seed,
          controllers: {
            player: { kind: 'human', name: t('match.you') },
            [match.config.seating.players[1]]: {
              kind: 'ai',
              name: opponentName(table),
              difficulty: tableDifficulty(match.night, match.kind),
              personality,
            },
          },
        },
        {
          ai,
          delay,
          actions: match.actions,
          onAction: (_before, action, after, evts) => {
            let next = recordNightAction(profileRef.current, action);
            if (after.phase === 'MATCH_OVER' && after.result) {
              const m = next.ledger!.run!.match!;
              next = finishNightTable(next, { final: after, events: tableEvents(m) });
            }
            setProfile(next);
            logger?.afterAction(evts);
          },
          onDispose: () => logger?.leave(),
        },
      );
      // Long Night tables are logged as campaign tables (src/app/matchLog.ts): the table number
      // is the Night times ten plus the table, and the opponent's name stays plain ASCII.
      logger = new MatchLogger(
        hostSource(h, {
          id: `night-${match.seed}`,
          mode: 'campaign',
          startedAt: new Date().toISOString(),
          campaign: {
            run: run.number,
            table: match.night * 10 + run.table + 1,
            regular: opponentName(table)
              .replace(/[^\x20-\x7e]/g, "'")
              .slice(0, 80),
          },
        }),
      );
      setHost(h);
      return h;
    },
    [ai, delay, setProfile],
  );

  // Resume a table in progress when the screen opens.
  useEffect(() => {
    const p = profileRef.current;
    if (p.ledger?.run?.phase !== 'playing' || !p.ledger.run.match) return;
    const resumed = openTable(p);
    return () => resumed?.dispose();
    // Only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => () => host?.dispose(), [host]);
  useEffect(() => () => puzzle?.dispose(), [puzzle]);
  /** The Puzzle Box's best chain is scored against its target, and the Detour is done. */
  const finishPuzzle = useCallback(() => {
    if (!puzzle) return;
    let score = 0;
    for (const e of puzzle.getSnapshot().log)
      if (e.type === 'ChainScored' && e.side === 'S') score = Math.max(score, e.breakdown.score);
    puzzle.dispose();
    setPuzzle(null);
    apply((p) => solvePuzzle(p, score));
  }, [puzzle, apply]);
  // The Puzzle Box is one turn: once it has passed to the other side, it is scored.
  useEffect(() => {
    if (!puzzle) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const check = () => {
      const snap = puzzle.getSnapshot();
      if (timer || (snap.view.active === 'S' && snap.view.phase !== 'MATCH_OVER')) return;
      timer = setTimeout(finishPuzzle, 2600);
    };
    const stop = puzzle.subscribe(check);
    return () => {
      stop();
      if (timer) clearTimeout(timer);
    };
  }, [puzzle, finishPuzzle]);

  const l = profile.ledger!;
  const run = l.run;

  // The scene behind each part of the run (docs/LONG_NIGHT_ART_DIRECTION.md §6.4–§6.5): the
  // smoke, the lamp and the stars, and the room tone (a clock on the Night board).
  const scene = sceneOf(run, !!host && run?.phase === 'playing');
  const sceneKey = JSON.stringify(scene);
  useEffect(() => {
    setScene(JSON.parse(sceneKey) as Partial<Scene>);
    sounds.setAmbience(run?.phase === 'board' || run?.phase === 'fielding' ? 'clock' : 'room');
  }, [sceneKey, run?.phase]);
  useEffect(
    () => () => {
      resetScene();
      sounds.setAmbience(null);
    },
    [],
  );

  // ---- The Puzzle Box ---------------------------------------------------------------------------
  if (puzzle && run?.detour?.puzzle) {
    const target = run.detour.puzzle.target;
    return (
      <MatchScreen
        host={puzzle}
        settings={settings}
        reduceMotion={reduceMotion}
        exitLabel={t('night.puzzle.done')}
        title={t('night.puzzle.title', { n: target })}
        canConcede={false}
        showRoundEnd={false}
        onExit={finishPuzzle}
        onSaveAndExit={finishPuzzle}
      />
    );
  }

  // ---- A table in progress ----------------------------------------------------------------------
  if (host && run && (run.phase === 'playing' || !showResult)) {
    // The opponent's name is on their strip: the bar only says which Night (or Hour) this is.
    const title =
      run.night > LONG_NIGHT.NIGHTS + 1
        ? t('night.hourShort', { n: run.night - LONG_NIGHT.NIGHTS - 1 })
        : nightLabel(run.night);
    return (
      <MatchScreen
        host={host}
        settings={settings}
        reduceMotion={reduceMotion}
        exitLabel={t('action.next')}
        title={title}
        nightTally={{
          night: run.night,
          bestChain: run.bestChain,
          ink: currentTable(run).kind === 'closing' ? 'N' : null,
          tint:
            currentTable(run).regular !== undefined
              ? REGULAR_LAMP[currentTable(run).regular!]
              : undefined,
          prop: opponentProp(currentTable(run)),
          // His own kit, and his man's, are kept under glass; a regular's or patron's is theirs.
          glass: currentTable(run).regular === undefined && !currentTable(run).patron,
          marks: pawnMarks(profile),
        }}
        showMarks
        saveWarning={saveError}
        onExit={() => {
          const over = host.getSnapshot().view.phase === 'MATCH_OVER';
          host.dispose();
          setHost(null);
          setShowResult(over);
          if (!over) onExit(saveError ?? undefined);
        }}
        onSaveAndExit={() => {
          host.dispose();
          setHost(null);
          onExit(saveError ?? undefined);
        }}
        onExport={() => onExportReplay(host.replay)}
      />
    );
  }

  const outcome = run?.outcomes[run.outcomes.length - 1];
  if (run && run.night !== shownNight && (run.phase === 'board' || run.phase === 'fielding')) {
    setShownNight(run.night);
    if (run.night > shownNight) setTitleNight(run.night);
  }

  const content = (() => {
    if (showResult && outcome && run)
      return outcome.won ? (
        <Receipt
          profile={profile}
          run={run}
          outcome={outcome}
          reduceMotion={reduceMotion}
          onContinue={() => setShowResult(false)}
        />
      ) : (
        <TableResult
          profile={profile}
          run={run}
          outcome={outcome}
          onContinue={() => setShowResult(false)}
        />
      );
    if (!run)
      return (
        <HubScene
          key={profile.seed}
          profile={profile}
          reduceMotion={reduceMotion}
          onStart={(ids, opts) => apply((p) => startNightRun(p, ids, opts))}
          onLedger={(page) => setBook(page === 'his' ? 'his' : 'superstitions')}
        />
      );
    switch (run.phase) {
      case 'board':
      case 'fielding':
        return (
          <BoardScene
            profile={profile}
            run={run}
            apply={apply}
            reduceMotion={reduceMotion}
            onPlay={(fielding) => {
              setError(null);
              try {
                let next = profileRef.current;
                if (fielding) next = setFielding(next, fielding);
                next = fieldNight(next);
                setProfile(next);
                openTable(next);
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
              }
            }}
            onAbandon={() => setConfirm('abandon')}
          />
        );
      case 'playing':
        return (
          <NightScreen title={nightLabel(run.night)} lead={t('night.tableWaiting')}>
            <button
              type="button"
              className="btn btn-primary btn-large"
              onClick={() => openTable(profile)}
            >
              {t('campaign.continueRun')}
            </button>
          </NightScreen>
        );
      case 'counter':
        return (
          <CounterScene
            profile={profile}
            run={run}
            apply={apply}
            reduceMotion={reduceMotion}
            onAbandon={() => setConfirm('abandon')}
          />
        );
      case 'spoils':
        return (
          <SpoilsScene profile={profile} run={run} apply={apply} reduceMotion={reduceMotion} />
        );
      case 'detour':
        return (
          <DetourScene
            profile={profile}
            run={run}
            apply={apply}
            reduceMotion={reduceMotion}
            onPuzzle={() => {
              const spec = run.detour?.puzzle;
              if (!spec) return;
              try {
                const position = puzzlePosition(run, spec);
                setPuzzle(
                  new MatchHost(
                    {
                      config: position.config,
                      seed: spec.seed,
                      controllers: {
                        player: { kind: 'human', name: t('match.you') },
                        puzzle: { kind: 'ai', name: t('night.puzzle.box'), difficulty: 'easy' },
                      },
                    },
                    { ai, delay, initialState: position },
                  ),
                );
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
              }
            }}
          />
        );
      default:
        return (
          <RunEndScene profile={profile} run={run} apply={apply} reduceMotion={reduceMotion} />
        );
    }
  })();

  return (
    <main className="page page-night">
      <header className="ln-topbar">
        <button
          type="button"
          className="ln-icon-btn"
          onClick={() => onExit()}
          aria-label={t('menu.back')}
          title={t('menu.back')}
        >
          <NightIcon name="arrow-left" size={20} />
        </button>
        <span className="ln-topbar-title">{t('night.title')}</span>
        <button
          type="button"
          className="ln-icon-btn"
          onClick={() => setGuide(true)}
          aria-label={t('night.howItWorks')}
          title={t('night.howItWorks')}
        >
          <NightIcon name="info" size={20} />
        </button>
      </header>
      {error && (
        <p className="notice ln-error" role="alert">
          {error}
        </p>
      )}
      <Rise id={`${run?.phase ?? 'hub'}-${run?.night ?? 0}-${run?.table ?? 0}-${showResult}`}>
        {content}
      </Rise>
      {titleNight !== null && (
        <NightTitleCard
          title={nightTitle(titleNight)}
          night={titleNight}
          still={reduceMotion}
          onDone={() => setTitleNight(null)}
        />
      )}
      {book && <LedgerBook profile={profile} page={book} onClose={() => setBook(null)} />}
      {guide && (
        <NightGuide
          onClose={() => {
            markGuideSeen('night');
            setGuide(false);
          }}
        />
      )}
      {confirm === 'abandon' && (
        <ConfirmDialog
          title={t('campaign.abandonRun')}
          body={t('night.abandonConfirm')}
          confirmLabel={t('campaign.abandonRun')}
          danger
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            setConfirm(null);
            apply(abandonNightRun);
          }}
        />
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------------------------
// After a table, and the end of the run
// ---------------------------------------------------------------------------------------------

function TableResult({
  profile,
  run,
  outcome,
  onContinue,
}: {
  profile: Profile;
  run: NightRun;
  outcome: NightOutcome;
  onContinue: () => void;
}) {
  const names = (ids: readonly string[]) =>
    ids.map((id) => profile.pawns[id]?.name ?? id).join(', ');
  const b = outcome.brass;
  const lines: [string, number][] = [
    [t('night.brass.table'), b.table],
    [t('night.brass.thrift'), b.thrift],
    [t('night.brass.interest'), b.interest],
    [t('night.brass.items'), b.items],
    [t('night.brass.match'), b.match],
  ];
  return (
    <NightScreen
      eyebrow={`${nightLabel(outcome.night)} · ${tableKindName(outcome.kind)}`}
      title={outcome.won ? t('campaign.won.title') : t('campaign.lost.title')}
      dock={
        <button
          type="button"
          className="btn btn-primary btn-large ln-dock-main"
          autoFocus
          onClick={onContinue}
        >
          {t('action.next')}
          <NightIcon name="arrow-right" size={18} />
        </button>
      }
    >
      <div className={`ln-score ${outcome.won ? 'is-won' : 'is-lost'}`}>
        <div>
          <span className="ln-eyebrow">{t('match.you')}</span>
          <strong>{formatScore(outcome.scores.S)}</strong>
        </div>
        <span className="ln-score-vs">–</span>
        <div>
          <span className="ln-eyebrow">{t('night.them')}</span>
          <strong>{formatScore(outcome.scores.N)}</strong>
        </div>
      </div>
      {(outcome.ante > 0 || outcome.bestChain > 0) && (
        <p className="ln-hint ln-center">
          {[
            outcome.ante > 0 ? t('night.anteWas', { n: formatScore(outcome.ante) }) : '',
            outcome.bestChain > 0
              ? t('night.bestChainHere', { n: formatScore(outcome.bestChain) })
              : '',
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}
      {outcome.won ? (
        <Section title={t('night.earned')} hint={t('night.earnedHint')}>
          <ul className="ln-rows">
            {lines
              .filter(([, n]) => n !== 0)
              .map(([label, n]) => (
                <li key={label}>
                  <span>{label}</span>
                  <strong className="ln-gold">
                    {n > 0 ? '+' : '−'}¤{Math.abs(n)}
                  </strong>
                </li>
              ))}
            <li className="ln-total">
              <span>{t('night.nowHave')}</span>
              <strong className="ln-gold">¤{run.brass}</strong>
            </li>
          </ul>
          {outcome.reclaimed.length > 0 && (
            <p>{t('campaign.reclaimed', { names: names(outcome.reclaimed) })}</p>
          )}
          {outcome.recruited.length > 0 && (
            <p>{t('campaign.recruited', { names: names(outcome.recruited) })}</p>
          )}
        </Section>
      ) : outcome.lost.length + outcome.vanished.length === 0 ? null : (
        <>
          {outcome.lost.length > 0 && (
            <p className="ln-sheet-note">
              {t('campaign.lostPawns', { names: names(outcome.lost) })}
            </p>
          )}
          {outcome.vanished.length > 0 && (
            <p className="ln-hint">{t('campaign.vanished', { n: outcome.vanished.length })}</p>
          )}
        </>
      )}
    </NightScreen>
  );
}
